/**
 * Header bands and freshness marks — `docs/plans/screener-bands.md`.
 *
 * Two marks on a screener header, both per column because columns can sit
 * anywhere in a screen:
 *
 * - **A band over it**: one band per run of adjacent columns sharing one. Its
 *   text is the user's name for it (`ScreenerColumn.band`) or, when nobody typed
 *   one, the column's **category** — `Price`, `Valuation`, `Momentum`. In front
 *   of the text, the **period** in small caps (`w`, `2w`, `m`); daily has none.
 * - **A line under it**, while the market is live: yellow for a live field,
 *   blue for a computed column. The window is the host's to decide (it has the
 *   clock and the calendar), so this module only says which kind a column is.
 *
 * Pure: the grid wraps its `ColDef`s with {@link groupIntoBands} and paints the
 * lines from the class {@link markClass} names.
 */

import type { ColDef, ColGroupDef } from "ag-grid-community";
import type { ScreenerColumn, ScreenerExpr } from "@qufolio/model";
import { formatTimeframe, type ScreenerColumnMeta, type ScreenerRow } from "@qufolio/transport";

/** What a column's header band says: its name and its period (`""` = daily). */
export interface Band {
  name: string;
  period: string;
}

/** Looks up an indicator function's catalog category — the host's catalog. */
export type CategoryOf = (fn: string) => string | undefined;

/** A {@link CategoryOf} over the host's DSL catalog, or undefined without one. */
export function categoryLookup(
  catalog: { functions: readonly { name: string; category?: string }[] } | undefined,
): CategoryOf | undefined {
  if (!catalog) return undefined;
  const byName = new Map(catalog.functions.map((f) => [f.name, f.category]));
  return (fn) => byName.get(fn);
}

/** `Momentum Indicators` → `Momentum`: a band is ~90 px, and every one says it. */
export function trimCategory(category: string | undefined): string {
  if (!category) return "";
  return category.endsWith(" Indicators") ? category.slice(0, -" Indicators".length) : category;
}

const FREQUENCY_RE = /^(\d*)(mi|s|h|d|w|m|q|y)$/;

/** `w`, `2w`, `m` — or `""` for daily and anything unreadable. */
function canonicalPeriod(frequency: string | undefined): string {
  if (!frequency) return "";
  const match = FREQUENCY_RE.exec(frequency);
  if (!match) return "";
  const count = match[1] === "" || match[1] === "1" ? "" : match[1]!;
  if (match[2] === "d" && count === "") return "";
  return `${count}${match[2]}`;
}

/**
 * The period a formula is about: the non-daily frequency its field references
 * use most, the first seen on a tie; `""` when every reference is daily.
 * A reference with no `ref` falls back to that field's own timeframe.
 */
export function periodOfExpr(
  expr: ScreenerExpr,
  metaOf?: (field: string) => ScreenerColumnMeta | undefined,
): string {
  const counts = new Map<string, number>();
  const walk = (node: ScreenerExpr): void => {
    if (node.kind === "field") {
      const fromRef = node.ref?.frequency;
      const meta = fromRef === undefined ? metaOf?.(node.field) : undefined;
      const period = fromRef !== undefined
        ? canonicalPeriod(fromRef)
        : meta?.timeframe ? formatTimeframe(meta.timeframe) : "";
      if (period) counts.set(period, (counts.get(period) ?? 0) + 1);
    } else if (node.kind === "binary") {
      walk(node.left);
      walk(node.right);
    } else if (node.kind === "call") {
      node.args.forEach(walk);
    }
  };
  walk(expr);
  let best = "";
  let most = 0;
  for (const [period, n] of counts) {
    if (n > most) {
      best = period;
      most = n;
    }
  }
  return best;
}

/** The first function a formula calls, depth first — what names its category. */
export function firstCall(expr: ScreenerExpr): string | undefined {
  if (expr.kind === "call") return expr.fn;
  if (expr.kind === "binary") return firstCall(expr.left) ?? firstCall(expr.right);
  return undefined;
}

/**
 * The band a column sits under, or `null` for none.
 *
 * Name: what the user typed, else the category — the server's for a base
 * column, the catalog's for a formula's first call. Period: the column's own
 * timeframe, or the formula's. A daily column with no category has no band.
 */
export function columnBand(
  column: ScreenerColumn | undefined,
  meta: ScreenerColumnMeta | undefined,
  categoryOf?: CategoryOf,
  metaOf?: (field: string) => ScreenerColumnMeta | undefined,
): Band | null {
  const expr = column?.computed?.expression;
  const period = meta?.timeframe
    ? formatTimeframe(meta.timeframe)
    : expr ? periodOfExpr(expr, metaOf) : "";
  const typed = column?.band?.trim();
  const fn = expr ? firstCall(expr) : undefined;
  const name = typed || trimCategory(expr ? (fn ? categoryOf?.(fn) : undefined) : meta?.category);
  if (!name && !period) return null;
  return { name, period };
}

/** Which line a header gets while the market is live, if any. */
export type Freshness = "live" | "computed";

export function freshnessOf(
  column: ScreenerColumn | undefined,
  meta: ScreenerColumnMeta | undefined,
): Freshness | undefined {
  if (column?.computed) return "computed";
  if (meta?.live) return "live";
  return undefined;
}

/** The header class that carries the line; the grid root's class switches it on. */
export function markClass(freshness: Freshness | undefined): string | undefined {
  return freshness ? `qf-mark-${freshness}` : undefined;
}

/** On the grid root while the market is live; the lines show only under it. */
export const LIVE_WINDOW_CLASS = "qf-live-window";

/** The band's words, for its tooltip: `Weekly · Momentum`. */
export function bandTitle(band: Band): string {
  const words: Record<string, string> = {
    s: "second", mi: "minute", h: "hour", d: "day", w: "week", m: "month", q: "quarter", y: "year",
  };
  const match = FREQUENCY_RE.exec(band.period);
  const period = match
    ? `${match[1] || "1"} ${words[match[2]!] ?? match[2]}${match[1] && match[1] !== "1" ? "s" : ""}`
    : "";
  return [period, band.name].filter(Boolean).join(" · ");
}

const keyOf = (band: Band | null): string | null => (band ? `${band.period}\u0000${band.name}` : null);

/**
 * Wrap runs of adjacent columns sharing a band into AG Grid column groups.
 *
 * A **hidden** column joins whatever run it sits in rather than splitting it:
 * it draws nothing, and a band broken by an invisible column would read as two
 * bands of the same name side by side. Showing it again rebuilds the defs, and
 * it lands in its own band then. Columns with no band stay top-level.
 *
 * `marryChildren`, so a drag moves a band whole — the band is set in the
 * editor, never by where a column was dropped.
 */
export function groupIntoBands(
  defs: readonly ColDef<ScreenerRow>[],
  bandOf: (def: ColDef<ScreenerRow>) => Band | null,
  headerGroupComponent?: unknown,
): (ColDef<ScreenerRow> | ColGroupDef<ScreenerRow>)[] {
  const out: (ColDef<ScreenerRow> | ColGroupDef<ScreenerRow>)[] = [];
  let run: { band: Band | null; key: string | null; children: ColDef<ScreenerRow>[] } | null = null;
  let index = 0;

  const flush = () => {
    if (!run) return;
    if (run.band === null) {
      out.push(...run.children);
    } else {
      const group: ColGroupDef<ScreenerRow> = {
        groupId: `band:${index++}:${run.key}`,
        headerName: run.band.name,
        headerTooltip: bandTitle(run.band),
        marryChildren: true,
        children: run.children,
        headerGroupComponentParams: { period: run.band.period },
      };
      if (headerGroupComponent) group.headerGroupComponent = headerGroupComponent;
      out.push(group);
    }
    run = null;
  };

  for (const def of defs) {
    if (def.hide && run) {
      run.children.push(def);
      continue;
    }
    const band = bandOf(def);
    const key = keyOf(band);
    if (run && run.key === key) {
      run.children.push(def);
      continue;
    }
    flush();
    run = { band, key, children: [def] };
  }
  flush();
  return out;
}

/** Every leaf `ColDef` in grid order, groups flattened — what `columnsFromDefs` reads. */
export function leafDefs(
  defs: readonly (ColDef<ScreenerRow> | ColGroupDef<ScreenerRow>)[],
): ColDef<ScreenerRow>[] {
  const out: ColDef<ScreenerRow>[] = [];
  for (const def of defs) {
    if ("children" in def && Array.isArray(def.children)) out.push(...leafDefs(def.children));
    else out.push(def as ColDef<ScreenerRow>);
  }
  return out;
}
